import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authService } from '../services/api';
import { 
  loginWithEmail, 
  loginWithGoogle, 
  registerWithEmail,
  logoutFirebase 
} from '../services/firebase';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth debe usarse dentro de AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Verificar token al cargar la aplicación
  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('nx2026_token');
      const savedUser = localStorage.getItem('nx2026_user');
      
      if (token && savedUser) {
        try {
          // Intentar obtener usuario actual desde el servidor
          const response = await authService.getCurrentUser();
          if (response.success) {
            setUser(response.user);
            localStorage.setItem('nx2026_user', JSON.stringify(response.user));
            
            // Guardar información de módulos si está disponible
            if (response.modules) {
              localStorage.setItem('nx2026_modules', JSON.stringify(response.modules));
            }
            if (response.showSelectionDialog !== undefined) {
              localStorage.setItem('nx2026_showSelectionDialog', JSON.stringify(response.showSelectionDialog));
            }
          } else {
            // Token inválido, limpiar
            localStorage.removeItem('nx2026_token');
            localStorage.removeItem('nx2026_user');
            localStorage.removeItem('nx2026_modules');
            localStorage.removeItem('nx2026_showSelectionDialog');
            localStorage.removeItem('nx2026_authorized_modules');
          }
        } catch (err) {
          // Si falla el servidor, usar datos guardados localmente
          console.warn('No se pudo verificar token con el servidor:', err.message);
          try {
            setUser(JSON.parse(savedUser));
          } catch (e) {
            localStorage.removeItem('nx2026_token');
            localStorage.removeItem('nx2026_user');
            localStorage.removeItem('nx2026_modules');
            localStorage.removeItem('nx2026_showSelectionDialog');
          }
        }
      }
      setLoading(false);
    };

    checkAuth();
  }, []);

  // Login con email/password usando Firebase Auth
  const login = useCallback(async (email, password) => {
    setError(null);
    setLoading(true);
    
    try {
      // Primero, autenticar con Firebase
      const firebaseResult = await loginWithEmail(email, password);
      
      if (!firebaseResult.success) {
        setError(firebaseResult.error);
        setLoading(false);
        return { success: false, error: firebaseResult.error };
      }
      
      // Enviar el token de Firebase al backend para generar JWT interno
      const response = await authService.loginWithFirebase(firebaseResult.idToken);
      
      if (response.success) {
        // Guardar token y usuario
        localStorage.setItem('nx2026_token', response.token);
        localStorage.setItem('nx2026_user', JSON.stringify(response.user));
        
        // Guardar información de módulos y si mostrar diálogo de selección
        if (response.modules) {
          localStorage.setItem('nx2026_modules', JSON.stringify(response.modules));
          
          // Guardar lista de módulos autorizados (POS, FACTURACION)
          const authorizedModules = [];
          response.modules.forEach(company => {
            if (company.modules && Array.isArray(company.modules)) {
              company.modules.forEach(mod => {
                const modId = mod.id ? mod.id.toUpperCase() : '';
                if (modId.includes('BILLING') && !authorizedModules.includes('FACTURACION')) {
                  authorizedModules.push('FACTURACION');
                } else if (modId.includes('POS') && !authorizedModules.includes('POS')) {
                  authorizedModules.push('POS');
                }
              });
            }
          });
          localStorage.setItem('nx2026_authorized_modules', JSON.stringify(authorizedModules));
        }
        if (response.showSelectionDialog !== undefined) {
          localStorage.setItem('nx2026_showSelectionDialog', JSON.stringify(response.showSelectionDialog));
        }
        
        setUser(response.user);
        setLoading(false);
        
        // Notificar al AppContext que el login fue exitoso
        window.dispatchEvent(new Event('nx2026-login-success'));
        
        // Si solo hay un módulo, seleccionar automáticamente
        if (response.modules && response.modules.length === 1 && response.showSelectionDialog === false) {
          const singleModule = response.modules[0];
          // Determinar el modo basado en el primer módulo disponible
          const modo = (singleModule.modules && singleModule.modules.length > 0 && 
            singleModule.modules[0].id.toUpperCase().includes('BILLING')) ? 'FACTURACION' : 'POS';
          
          // Guardar el modo en localStorage para el día de hoy
          const today = new Date();
          const todayKey = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
          localStorage.setItem(`nx2026_modo_${todayKey}`, modo);
          
          // Notificar al AppContext para que actualice el modo
          window.dispatchEvent(new CustomEvent('nx2026-modo-seleccionado', { detail: { modo } }));
        }
        
        return { success: true };
      } else {
        // Error del backend
        setError(response.error || 'Error al iniciar sesión');
        setLoading(false);
        return { success: false, error: response.error };
      }
    } catch (err) {
      const errorMessage = err.response?.data?.error || err.message || 'Error de conexión';
      setError(errorMessage);
      setLoading(false);
      return { success: false, error: errorMessage };
    }
  }, []);

  // Login con Google OAuth usando Firebase
  const loginGoogle = useCallback(async () => {
    setError(null);
    setLoading(true);
    
    try {
      // Autenticar con Google usando Firebase Popup
      const firebaseResult = await loginWithGoogle();
      
      if (!firebaseResult.success) {
        setError(firebaseResult.error);
        setLoading(false);
        return { success: false, error: firebaseResult.error };
      }
      
      // Enviar el token de Google al backend
      const response = await authService.loginGoogle(firebaseResult.idToken);
      
      if (response.success) {
        localStorage.setItem('nx2026_token', response.token);
        localStorage.setItem('nx2026_user', JSON.stringify(response.user));
        
        // Guardar información de módulos y si mostrar diálogo de selección
        if (response.modules) {
          localStorage.setItem('nx2026_modules', JSON.stringify(response.modules));
          
          // Guardar lista de módulos autorizados (POS, FACTURACION)
          const authorizedModules = [];
          response.modules.forEach(company => {
            if (company.modules && Array.isArray(company.modules)) {
              company.modules.forEach(mod => {
                const modId = mod.id ? mod.id.toUpperCase() : '';
                if (modId.includes('BILLING') && !authorizedModules.includes('FACTURACION')) {
                  authorizedModules.push('FACTURACION');
                } else if (modId.includes('POS') && !authorizedModules.includes('POS')) {
                  authorizedModules.push('POS');
                }
              });
            }
          });
          localStorage.setItem('nx2026_authorized_modules', JSON.stringify(authorizedModules));
        }
        if (response.showSelectionDialog !== undefined) {
          localStorage.setItem('nx2026_showSelectionDialog', JSON.stringify(response.showSelectionDialog));
        }
        
        setUser(response.user);
        setLoading(false);
        
        // Notificar al AppContext que el login fue exitoso
        window.dispatchEvent(new Event('nx2026-login-success'));
        
        // Si solo hay un módulo, seleccionar automáticamente
        if (response.modules && response.modules.length === 1 && response.showSelectionDialog === false) {
          const singleModule = response.modules[0];
          // Determinar el modo basado en el primer módulo disponible
          const modo = (singleModule.modules && singleModule.modules.length > 0 && 
            singleModule.modules[0].id.toUpperCase().includes('BILLING')) ? 'FACTURACION' : 'POS';
          
          // Guardar el modo en localStorage para el día de hoy
          const today = new Date();
          const todayKey = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
          localStorage.setItem(`nx2026_modo_${todayKey}`, modo);
          
          // Notificar al AppContext para que actualice el modo
          window.dispatchEvent(new CustomEvent('nx2026-modo-seleccionado', { detail: { modo } }));
        }
        
        return { success: true };
      } else {
        setError(response.error || 'Error con Google OAuth');
        setLoading(false);
        return { success: false, error: response.error };
      }
    } catch (err) {
      const errorMessage = err.response?.data?.error || err.message || 'Error de conexión';
      setError(errorMessage);
      setLoading(false);
      return { success: false, error: errorMessage };
    }
  }, []);

  // Registrar nuevo usuario
  const register = useCallback(async (email, password, nombre) => {
    setError(null);
    setLoading(true);
    
    try {
      // Registrar en Firebase
      const firebaseResult = await registerWithEmail(email, password, nombre);
      
      if (!firebaseResult.success) {
        setError(firebaseResult.error);
        setLoading(false);
        return { success: false, error: firebaseResult.error };
      }
      
      // Enviar el token de Firebase al backend
      const response = await authService.registerWithFirebase(firebaseResult.idToken, nombre);
      
      if (response.success) {
        localStorage.setItem('nx2026_token', response.token);
        localStorage.setItem('nx2026_user', JSON.stringify(response.user));
        setUser(response.user);
        setLoading(false);
        
        // Notificar al AppContext que el registro/login fue exitoso
        window.dispatchEvent(new Event('nx2026-login-success'));
        
        return { success: true };
      } else {
        setError(response.error || 'Error al registrar usuario');
        setLoading(false);
        return { success: false, error: response.error };
      }
    } catch (err) {
      const errorMessage = err.response?.data?.error || err.message || 'Error de conexión';
      setError(errorMessage);
      setLoading(false);
      return { success: false, error: errorMessage };
    }
  }, []);

  // Logout
  const logout = useCallback(async () => {
    try {
      // Cerrar sesión en Firebase
      await logoutFirebase();
    } catch (err) {
      console.warn('Error en logout de Firebase:', err.message);
    } finally {
      try {
        await authService.logout();
      } catch (err) {
        console.warn('Error en logout:', err.message);
      } finally {
        localStorage.removeItem('nx2026_token');
        localStorage.removeItem('nx2026_user');
        localStorage.removeItem('nx2026_modules');
        localStorage.removeItem('nx2026_showSelectionDialog');
        localStorage.removeItem('nx2026_authorized_modules');
        setUser(null);
        setError(null);
      }
    }
  }, []);

  // Obtener usuario actual
  const getCurrentUser = useCallback(async () => {
    try {
      const response = await authService.getCurrentUser();
      if (response.success) {
        setUser(response.user);
        return response.user;
      }
      return null;
    } catch (err) {
      console.error('Error al obtener usuario:', err);
      return null;
    }
  }, []);

  const isAuthenticated = !!user;

  const value = {
    user,
    loading,
    error,
    isAuthenticated,
    login,
    loginGoogle,
    register,
    logout,
    getCurrentUser,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
